import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon, GlobeIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
export function Settings() {
  const {
    settings,
    settingsLoading,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setSaved(false);
        setError('');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1015" data-mp="2erz6e60g8uj3">
      <p className="text-sm text-ink-muted" data-id="element-1016" data-mp="2etaa65twa2a7">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1017" data-mp="2ssa15k4vqt9j">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-1018" data-mp="31rx2k5xlzz2l">
        Oplysningerne herunder er hjemmesidens ene fælles kilde. De bruges automatisk i topbjælken,
        hero-sektionen, kontaktafsnittet og footeren — du retter dem kun ét sted.
      </p>

      <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1019" data-mp="3bh6rxe12j3n0">
        <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1020" data-mp="3lg5ssaflduzg" />
        Oplysningerne gemmes globalt og vises for alle besøgende på alle enheder.
      </p>

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      const result = await saveSettings(draft);
      setSaving(false);
      if (result.ok) {
        setSaved(true);
        setError('');
      } else {
        setSaved(false);
        setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      }
    }} data-id="element-1021" data-mp="3geylsdl9wztu">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-1022" data-mp="b2l1yy7fmenh">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1023" data-mp="f34vlrssr04v">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1024" data-mp="k4u9df7elrwn">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-1025" data-mp="p2dmucwa20nj" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-1026" data-mp="p09jbw2aj3ns" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-1027" data-mp="2lx0864n8eutf" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1028" data-mp="2jazi4fbm1677">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1029" data-mp="2ejrddwl8690d">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1030" data-mp="29kepr9mj4xwq">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-1031" data-mp="29kxwrvan61vz" />
            <TextField id="in-mail" label="E-mail" type="email" readOnly disabled value={CONTACT_EMAIL} onChange={() => undefined} hint="Fast adresse — kan ikke ændres." data-id="element-1032" data-mp="2x9pkoah77y2y" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-1033" data-mp="1gwijpeb2ltna" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-1034" data-mp="1bweam9chs173" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1035" data-mp="30piqhfii7fzh">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1036" data-mp="35iva1b8bswld">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1037" data-mp="2qtysmnkdq783">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-1038" data-mp="2vs0efg96wkn9" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-1039" data-mp="3jhupptdv215e" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-1040" data-mp="39jnkg7sxp7j8">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-1041" data-mp="1y48fagqkn9d1">
          <Button type="submit" size="lg" disabled={!dirty || saving || settingsLoading} data-id="element-1042" data-mp="1y2oz0q4x0yb9">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1043" data-mp="l5pzpeu095ab" />
                Gemmer …
              </> : 'Gem ændringer'}
          </Button>

          {settingsLoading && <p className="text-[15px] text-ink-muted" data-id="element-1044" data-mp="u6whrscayo5o">Henter aktuelle oplysninger …</p>}

          {dirty && !saving && !saved && !error && <p className="text-[15px] text-ink-muted" data-id="element-1045" data-mp="q5krdklltywf">Der er ændringer, der ikke er gemt.</p>}

          {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1046" data-mp="3tli523iz5ozi">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1047" data-mp="3tjovafd2eona">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1048" data-mp="6h263e3pz5n0" />
              </span>
              Ændringerne er gemt og opdateret på hjemmesiden
            </p>}

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1049" data-mp="3avjxrpn03zwx">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1050" data-mp="1dunt91q9caqa" />
              {error}
            </p>}
        </div>
      </form>

      <SecuritySection data-id="element-1051" data-mp="1iro1njakxpkk" />
    </div>;
}